import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal, SectionHeading, Counter, Icon, Marquee, staggerContainer, staggerItem } from "@/components/common/motion";
import { stats, clients, whyChooseUs, services, industries, process, technologies, blog, caseStudies, trustBadges } from "@/data/site";
import { cn } from "@/lib/utils";

export function StatsBand() {
  return (
    <section className="border-y border-border bg-card py-16" data-testid="stats-band">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-5 sm:px-8 lg:grid-cols-4">
        {stats.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08} className="text-center lg:text-left">
            <p className="font-display text-5xl font-semibold tracking-tight text-foreground md:text-6xl">
              <Counter value={s.value} suffix={s.suffix} />
            </p>
            <p className="mt-2 text-sm text-muted-foreground">{s.label}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function ClientsMarquee() {
  return (
    <section className="border-b border-border py-10" data-testid="clients-marquee">
      <p className="mb-6 text-center text-xs font-semibold uppercase tracking-[0.25em] text-muted-foreground">
        Powering teams across industries
      </p>
      <Marquee
        duration={38}
        items={clients.map((c) => (
          <span key={c} className="mx-10 font-display text-2xl font-medium text-muted-foreground/60 transition-colors hover:text-foreground">
            {c}
          </span>
        ))}
      />
    </section>
  );
}

export function EditorialMarquee() {
  const word = (
    <span className="mx-8 flex items-center gap-8 font-display text-5xl font-medium tracking-tight md:text-7xl">
      Empowering Businesses <span className="text-primary">✦</span> Through Smart Technology <span className="text-accent">✦</span>
    </span>
  );
  return (
    <section className="overflow-hidden border-y border-border bg-secondary py-8 text-secondary-foreground" data-testid="editorial-marquee">
      <Marquee duration={30} items={[word, word]} />
    </section>
  );
}

export function WhyChooseUs() {
  return (
    <section className="py-24 sm:py-32" data-testid="why-choose-us">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <SectionHeading overline="Why NeuroTriq" title="A partner built for outcomes, not just output" subtitle="We combine senior talent, transparent delivery and security-first engineering to move your business forward." />
        </Reveal>
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
        >
          {whyChooseUs.map((w) => (
            <motion.div
              key={w.title}
              variants={staggerItem}
              className="group rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg"
              data-testid={`why-${w.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                <Icon name={w.icon} className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-display text-lg font-medium">{w.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{w.text}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export function ServicesPreview() {
  return (
    <section className="bg-muted py-24 sm:py-32" data-testid="services-preview">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <SectionHeading overline="What we do" title="End-to-end technology services" subtitle="From strategy to support — one accountable partner across the entire lifecycle." />
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/services" className="group inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium transition-colors hover:border-primary/50" data-testid="services-view-all">
              View all services <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 3) * 0.06}>
              <Link
                to={`/services#${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="relative h-40 overflow-hidden">
                  <img src={s.image} alt={s.title} loading="lazy" className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:scale-105 group-hover:grayscale-0" />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent" />
                  <span className="absolute left-4 top-4 grid h-11 w-11 place-items-center rounded-xl bg-primary text-primary-foreground shadow-lg">
                    <Icon name={s.icon} className="h-5 w-5" />
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display text-xl font-medium">{s.title}</h3>
                    <ArrowUpRight className="h-5 w-5 shrink-0 text-muted-foreground transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary" />
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.description}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {s.features.slice(0, 3).map((f) => (
                      <span key={f} className="rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">{f}</span>
                    ))}
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function IndustriesPreview() {
  return (
    <section className="py-24 sm:py-32" data-testid="industries-preview">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <SectionHeading overline="Industries" title="Deep expertise where it matters" subtitle="We bring domain knowledge to regulated, complex and fast-moving industries." />
        </Reveal>
        <div className="mt-14 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {industries.slice(0, 9).map((ind, i) => (
            <Reveal key={ind.name} delay={(i % 3) * 0.05}>
              <div className="group flex items-start gap-4 rounded-2xl border border-border bg-card p-5 transition-all duration-300 hover:border-primary/40 hover:bg-primary/[0.03]" data-testid={`industry-${ind.name.toLowerCase()}`}>
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-accent/10 text-accent transition-colors group-hover:bg-accent group-hover:text-accent-foreground">
                  <Icon name={ind.icon} className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-display text-base font-medium">{ind.name}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{ind.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1} className="mt-10 text-center">
          <Link to="/industries" className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline" data-testid="industries-view-all">
            Explore all industries <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export function ProcessSection() {
  return (
    <section className="relative overflow-hidden bg-secondary py-24 text-secondary-foreground sm:py-32 grain" data-testid="process-section">
      <div className="pointer-events-none absolute right-0 top-0 h-80 w-80 rounded-full bg-primary/20 blur-[120px]" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="max-w-3xl">
            <div className="mb-4 flex items-center gap-2">
              <span className="h-px w-8 bg-accent" />
              <span className="text-xs font-bold uppercase tracking-[0.22em] text-accent">How we work</span>
            </div>
            <h2 className="font-display text-4xl font-medium tracking-tight text-white md:text-5xl">A proven process, radically transparent</h2>
            <p className="mt-5 text-lg text-white/70">Seven stages that de-risk delivery and keep you in control from first idea to ongoing support.</p>
          </div>
        </Reveal>

        <div className="mt-16 grid gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {process.map((p, i) => (
            <Reveal key={p.step} delay={(i % 4) * 0.06}>
              <div className="group h-full bg-secondary p-7 transition-colors hover:bg-white/[0.04]" data-testid={`process-${p.step}`}>
                <div className="flex items-center justify-between">
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary/20 text-primary-foreground">
                    <Icon name={p.icon} className="h-5 w-5 text-primary" />
                  </span>
                  <span className="font-display text-3xl font-semibold text-white/15">{p.step}</span>
                </div>
                <h3 className="mt-5 font-display text-lg font-medium text-white">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/60">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TechStackSection() {
  return (
    <section className="py-24 sm:py-32" data-testid="techstack-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <SectionHeading align="center" overline="Technology Stack" title="We build with the best tools for the job" subtitle="A modern, battle-tested stack across frontend, backend, cloud, data and AI." />
        </Reveal>
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="mx-auto mt-14 flex max-w-5xl flex-wrap justify-center gap-3"
        >
          {technologies.map((t) => (
            <motion.span
              key={t.name}
              variants={staggerItem}
              className="group flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-md"
              data-testid={`tech-${t.name.toLowerCase().replace(/[^a-z0-9]/g, "")}`}
            >
              <span className="h-2 w-2 rounded-full bg-gradient-to-br from-primary to-accent" />
              {t.name}
              <span className="text-xs text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">{t.category}</span>
            </motion.span>
          ))}
        </motion.div>
        <Reveal delay={0.1} className="mt-10 text-center">
          <Link to="/technologies" className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline" data-testid="tech-view-all">
            Explore our full stack <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export function CaseStudiesPreview() {
  return (
    <section className="bg-muted py-24 sm:py-32" data-testid="casestudies-preview">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <SectionHeading overline="Case Studies" title="Outcomes, measured" subtitle="A look at the results we've delivered for clients across industries." />
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/case-studies" className="group inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium transition-colors hover:border-primary/50" data-testid="casestudies-view-all">
              All case studies <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          {caseStudies.slice(0, 2).map((cs, i) => (
            <Reveal key={cs.id} delay={i * 0.1}>
              <Link to="/case-studies" className="group block overflow-hidden rounded-3xl border border-border bg-card transition-all duration-300 hover:shadow-xl" data-testid={`casestudy-preview-${cs.id}`}>
                <div className="relative h-56 overflow-hidden">
                  <img src={cs.image} alt={cs.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <span className="absolute left-4 top-4 rounded-full bg-card/90 px-3 py-1 text-xs font-medium backdrop-blur">{cs.industry}</span>
                </div>
                <div className="p-7">
                  <h3 className="font-display text-2xl font-medium tracking-tight">{cs.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{cs.challenge}</p>
                  <div className="mt-6 flex gap-8">
                    {cs.results.slice(0, 3).map((r) => (
                      <div key={r.label}>
                        <p className="font-display text-2xl font-semibold text-primary">{r.metric}</p>
                        <p className="text-xs text-muted-foreground">{r.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function BlogPreview() {
  return (
    <section className="py-24 sm:py-32" data-testid="blog-preview">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <SectionHeading overline="Insights" title="From our engineering desk" subtitle="Practical perspectives on AI, cloud, security and building great software." />
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/blog" className="group inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium transition-colors hover:border-primary/50" data-testid="blog-view-all">
              Read the blog <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {blog.slice(0, 3).map((b, i) => (
            <Reveal key={b.slug} delay={i * 0.08}>
              <Link to={`/blog/${b.slug}`} className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`blog-card-${b.slug}`}>
                <div className="relative h-48 overflow-hidden">
                  <img src={b.image} alt={b.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <span className="absolute left-4 top-4 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">{b.category}</span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <p className="text-xs text-muted-foreground">{b.date} · {b.readTime}</p>
                  <h3 className="mt-2 font-display text-lg font-medium leading-snug transition-colors group-hover:text-primary">{b.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{b.excerpt}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TrustBadges() {
  return (
    <section className="border-y border-border bg-card py-12" data-testid="trust-badges">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-5 sm:px-8 md:grid-cols-3 lg:grid-cols-6">
        {trustBadges.map((b) => (
          <div key={b.label} className="flex items-center gap-3 text-sm text-muted-foreground">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-success/10 text-success">
              <Icon name={b.icon} className="h-5 w-5" />
            </span>
            <span className="font-medium text-foreground">{b.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
